import React, { useState, useCallback } from 'react';
import { Text, TouchableOpacity, StyleSheet, Alert } from 'react-native';
import { usePlaidLink } from 'react-plaid-link';
import { API_ENDPOINTS } from '../../config/api';

const PlaidLink = ({ 
  onSuccess, 
  onError, 
  userId = 'demo_user',
  buttonText = 'Connect Bank Account',
  buttonStyle = {},
  textStyle = {},
  disabled = false 
}) => {
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);

  // Create link token when component mounts or when user clicks
  const createLinkToken = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.PLAID.CREATE_LINK_TOKEN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create link token');
      }

      setLinkToken(data.link_token);
      return data.link_token;
    } catch (error) {
      console.error('Error creating link token:', error);
      if (onError) {
        onError(error);
      } else {
        Alert.alert(
          'Connection Error',
          'Failed to prepare bank connection. Please check your internet connection and try again.',
          [{ text: 'OK' }]
        );
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId, onError]);

  // Handle successful link
  const handleOnSuccess = useCallback(async (public_token, metadata) => {
    try {
      setLoading(true);
      
      // Exchange public token for access token
      const response = await fetch(API_ENDPOINTS.PLAID.EXCHANGE_TOKEN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          public_token,
          userId 
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to connect bank account');
      }

      // Notify parent component of success
      if (onSuccess) {
        onSuccess({
          success: true,
          itemId: data.item_id,
          institution: metadata.institution,
          accounts: metadata.accounts,
          message: 'Bank account connected successfully!'
        });
      } else {
        Alert.alert(
          'Success!',
          'Your bank account has been connected successfully.',
          [{ text: 'OK' }]
        );
      }
    } catch (error) {
      console.error('Error exchanging token:', error);
      if (onError) {
        onError(error);
      } else {
        Alert.alert(
          'Connection Failed',
          'Failed to complete bank account connection. Please try again.',
          [{ text: 'OK' }]
        );
      }
    } finally {
      setLoading(false);
    }
  }, [userId, onSuccess, onError]);

  // Handle link errors
  const handleOnError = useCallback((error) => {
    console.error('Plaid Link error:', error);
    if (onError) {
      onError(error);
    } else {
      Alert.alert(
        'Connection Error',
        error.error_message || 'An error occurred while connecting your bank account.',
        [{ text: 'OK' }]
      );
    }
  }, [onError]);

  // Configure Plaid Link
  const config = {
    token: linkToken,
    onSuccess: handleOnSuccess,
    onError: handleOnError,
    onExit: (error, metadata) => {
      if (error) {
        console.log('User exited with error:', error);
      } else {
        console.log('User exited Link flow');
      }
    },
  };

  const { open, ready } = usePlaidLink(config);

  // Handle button press
  const handlePress = async () => {
    if (disabled || loading) return;

    if (!linkToken) {
      const token = await createLinkToken();
      if (!token) return;
    }

    if (ready) {
      open();
    }
  };

  return (
    <TouchableOpacity
      style={[
        styles.button,
        (disabled || loading) && styles.buttonDisabled,
        buttonStyle
      ]}
      onPress={handlePress}
      disabled={disabled || loading}
    >
      <Text style={[
        styles.buttonText,
        (disabled || loading) && styles.buttonTextDisabled,
        textStyle
      ]}>
        {loading ? 'Connecting...' : buttonText}
      </Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  button: {
    backgroundColor: '#2563EB',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
  buttonDisabled: {
    backgroundColor: '#9CA3AF',
    opacity: 0.6,
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  buttonTextDisabled: {
    color: '#E5E7EB',
  },
});

export default PlaidLink; 